import React, { useEffect, useState } from "react";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import RideDriverModal from "../components/RideDriverModal";
import { toast } from "react-hot-toast";
import { RefreshCw } from "lucide-react";

const API_BASE = process.env.REACT_APP_API_BASE; // ✅ unified API

const RideDocuments = () => {
  const [drivers, setDrivers] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  // ============================================================
  // 📡 Fetch Ride Driver Documents
  // ============================================================
  const fetchRideDocs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/admin/ride-docs`);
      setDrivers(res.data || []);
    } catch (err) {
      console.error("❌ Failed to fetch ride docs", err);
      toast.error("Failed to load ride driver documents.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRideDocs();
  }, []);

  // ============================================================
  // 🧾 Render Driver Status
  // ============================================================
  const renderStatus = (value) => {
    if (value === "approved")
      return <span className="text-green-600 font-semibold">✅ Approved</span>;
    if (value === "rejected")
      return <span className="text-red-600 font-semibold">❌ Rejected</span>;
    return <span className="text-yellow-600 font-semibold">⏳ Pending</span>;
  };

  // ============================================================
  // 🔍 Filter Drivers by Status
  // ============================================================
  const filteredDrivers = drivers.filter((d) =>
    filter === "all" ? true : d.driverProfile?.status === filter
  );

  // ============================================================
  // 🖥️ Render
  // ============================================================
  return (
    <div className="flex font-[Poppins]">
      <Sidebar />

      <main className="ml-0 md:ml-64 p-6 bg-gray-100 min-h-screen">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-3xl font-extrabold text-gray-800">
            🚗 Ride Driver Documents
          </h2>

          <button
            onClick={fetchRideDocs}
            disabled={loading}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg font-semibold disabled:opacity-70"
          >
            <RefreshCw
              className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
            />
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        {/* Filter Buttons */}
        <div className="mb-5 flex flex-wrap gap-3">
          {["all", "pending", "approved", "rejected"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg font-semibold text-sm transition ${
                filter === f
                  ? "bg-indigo-600 text-white"
                  : "bg-gray-200 text-gray-800 hover:bg-gray-300"
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {/* Table */}
        {loading ? (
          <p className="text-gray-600">Loading ride driver documents...</p>
        ) : filteredDrivers.length === 0 ? (
          <p className="text-gray-500">No drivers match this filter.</p>
        ) : (
          <div className="overflow-x-auto bg-white rounded-2xl shadow">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 text-gray-700 border-b">
                <tr>
                  <th className="py-3 px-4 text-left">Name</th>
                  <th className="py-3 px-4 text-left">Email</th>
                  <th className="py-3 px-4 text-left">Phone</th>
                  <th className="py-3 px-4 text-left">Vehicle</th>
                  <th className="py-3 px-4 text-left">Status</th>
                  <th className="py-3 px-4 text-left"># Rides</th>
                  <th className="py-3 px-4 text-left">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredDrivers.map((w, i) => (
                  <tr
                    key={i}
                    className="border-t hover:bg-gray-50 transition duration-150"
                  >
                    <td className="py-2 px-4 font-semibold text-gray-800">
                      {w.name || "Unnamed"}
                    </td>
                    <td className="py-2 px-4">{w.email}</td>
                    <td className="py-2 px-4">{w.phone || "—"}</td>
                    <td className="py-2 px-4">
                      {w.driverProfile?.year || ""}{" "}
                      {w.driverProfile?.make || ""}{" "}
                      {w.driverProfile?.model || ""}
                    </td>
                    <td className="py-2 px-4">{renderStatus(w.driverProfile?.status)}</td>
                    <td className="py-2 px-4">{w.rideCount || 0}</td>
                    <td className="py-2 px-4">
                      <button
                        onClick={() => setSelectedWorker(w)}
                        className="text-blue-600 underline hover:text-blue-800"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Modal */}
        {selectedWorker && (
          <RideDriverModal
            worker={selectedWorker}
            onClose={() => setSelectedWorker(null)}
          />
        )}
      </main>
    </div>
  );
};

export default RideDocuments;
